import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import React from 'react';
import {
  getArticleCategoriesInMenu,
  getCategoryBySlug,
  getPagedArticleByCategory,
} from 'services/cmsService';
import { IArticleResult } from 'types/pages';

import Typography from '@material-ui/core/Typography';
import Pagination from '@material-ui/lab/Pagination';

import ArticleListSection from 'components/CMS/ArticleListSection';
import { ArticleResultLanding } from 'components/CMS/ArticleResultLanding';

import { ContentHubContainer } from 'modules/ContentHubContainer';

import styles from './style.module.scss';

export const ARTICLE_RESULT_FIRST_PAGE_AMOUNT = 14;
export const ARTICLE_RESULT_REST_PAGES_AMOUNT = 18;

const ArticlesByCategory = (props: IArticleResult) => {
  const router = useRouter();

  const {
    articles,
    categoriesOnMenu,
    title,
    query,
    pageCount,
    promotion,
    slug,
    metaTitle,
    metaDescription,
  } = props;

  return (
    <ContentHubContainer categories={categoriesOnMenu} selectedCategorySlug={slug}>
      <NextSeo
        title={metaTitle ?? undefined}
        description={metaDescription ?? undefined}
        noindex={true}
        nofollow={true}
        openGraph={{
          title: metaTitle ?? undefined,
          description: metaDescription ?? undefined,
          type: 'article',
        }}
      />
      <div className={styles.container}>
        <Typography variant="h4" component="h1">
          {title}
        </Typography>
      </div>
      {query.page === 1 && <ArticleResultLanding articles={articles} promotion={promotion} />}
      {query.page > 1 && <ArticleListSection articles={articles} />}
      {pageCount > 1 && (
        <div className={styles.pagination}>
          <Pagination
            count={pageCount}
            page={+query.page || 1}
            variant="outlined"
            shape="rounded"
            color="primary"
            onChange={(e, value) => {
              query.page = value;
              router.push({ pathname: location.pathname, query });
            }}
          />
        </div>
      )}
    </ContentHubContainer>
  );
};

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<IArticleResult>> {
  try {
    const { slug, page = '1' } = ctx.query;

    const parsedPage = parseInt(page.toString(), 10);

    if (slug === undefined || isNaN(parsedPage) || parsedPage < 1) {
      return {
        notFound: true,
      };
    }

    const query = {
      page: page ? +page : 1,
    };
    const isFirstPage = page == '1';
    const currentPage = isFirstPage ? 1 : parsedPage;
    const start = isFirstPage
      ? 0
      : ARTICLE_RESULT_FIRST_PAGE_AMOUNT + (currentPage - 2) * ARTICLE_RESULT_REST_PAGES_AMOUNT;

    const [{ articles, total }, categoriesOnMenu, currentCategory] = await Promise.all([
      getPagedArticleByCategory(
        slug as string,
        isFirstPage ? ARTICLE_RESULT_FIRST_PAGE_AMOUNT : ARTICLE_RESULT_REST_PAGES_AMOUNT,
        start,
      ),
      getArticleCategoriesInMenu(),
      getCategoryBySlug(slug),
    ]);

    if (!currentCategory) {
      return { notFound: true };
    }
    const totalWithoutFirstPage = total - ARTICLE_RESULT_FIRST_PAGE_AMOUNT;
    const pageCount = Math.ceil(totalWithoutFirstPage / ARTICLE_RESULT_REST_PAGES_AMOUNT) + 1;

    return {
      props: {
        query,
        slug: slug as string,
        pageCount,
        articles: articles,
        categoriesOnMenu: categoriesOnMenu,
        title: currentCategory.name,
        promotion: currentCategory.article_promotion ?? null,
        metaTitle: currentCategory.meta_title,
        metaDescription: currentCategory.meta_description,
      },
    };
  } catch (error) {
    throw new Error('Failed to load Articles by Category', { cause: error });
  }
}

export default ArticlesByCategory;
